import { watch, onBeforeUnmount, toValue } from 'vue';

/**
 * Closes an overlay when its backdrop is clicked.
 *
 * Vuetify's `v-click-outside` directive registers its listeners on the module-global `document` — the
 * host page — so it never fires for an overlay teleported into the legacy iframe through `:attach`,
 * and backdrop clicks are simply ignored. This listens on the iframe's own document instead.
 *
 * @param {import('vue').MaybeRefOrGetter<boolean>} isOpen open state of the overlay
 * @param {() => void} close called when the backdrop is clicked
 * @param {HTMLElement|null} overlayTarget the injected `overlayTarget` (its document is listened to)
 * @param {import('vue').MaybeRefOrGetter<string>} rootSelector class selector identifying this
 *   overlay's `.v-overlay` root, so a nested overlay's scrim (a v-select menu opened inside the
 *   sheet) does not close the parent — reactive so one call can serve several mutually-exclusive
 *   overlay shapes (e.g. a component that renders either a bottom sheet or a side panel)
 */
export function useOverlayScrimClose(isOpen, close, overlayTarget, rootSelector) {
	let boundDocument = null;

	function onDocumentClick(event) {
		const target = event.target;
		if (!target?.classList?.contains('v-overlay__scrim')) {
			return;
		}

		const selector = toValue(rootSelector);
		if (selector && !target.closest(selector)) {
			return;
		}

		close();
	}

	function bind() {
		const ownerDocument = overlayTarget?.ownerDocument || (typeof document !== 'undefined' ? document : null);
		if (!ownerDocument || boundDocument) {
			return;
		}

		boundDocument = ownerDocument;
		boundDocument.addEventListener('click', onDocumentClick, true);
	}

	function unbind() {
		boundDocument?.removeEventListener('click', onDocumentClick, true);
		boundDocument = null;
	}

	watch(
		() => toValue(isOpen),
		(open) => (open ? bind() : unbind()),
		{ immediate: true }
	);

	onBeforeUnmount(unbind);
}
